import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, MapPin, Navigation, Maximize2, Minimize2, X } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const db = () => serviceRoleClient || supabase;

const parseCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const formatKm = (km) => {
  if (km == null || !Number.isFinite(km)) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
};

const extractLatLngFromText = (text) => {
  const s = String(text || '');
  const at = s.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (at) return parseCoord(at[1], at[2]);
  const q = s.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (q) return parseCoord(q[1], q[2]);
  const ll = s.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (ll) return parseCoord(ll[1], ll[2]);
  return null;
};

const fmtTime = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtAge = (value, nowMs) => {
  if (!value) return '';
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return '';
  const sec = Math.max(0, Math.round(((nowMs || Date.now()) - ts) / 1000));
  if (sec < 8) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return fmtTime(value);
};

const liveFreshness = (value, nowMs, closed) => {
  if (closed) return 'done';
  if (!value) return 'waiting';
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return 'waiting';
  const sec = Math.max(0, ((nowMs || Date.now()) - ts) / 1000);
  if (sec <= 45) return 'live';
  if (sec <= 180) return 'updating';
  return 'gap';
};

const cleanTrail = (points) => {
  if (!points.length) return [];
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const prev = out[out.length - 1];
    const km = haversineKm(prev, points[i]);
    if (km == null) continue;
    const dt = Math.max(
      1,
      (new Date(points[i].created_at || 0).getTime() - new Date(prev.created_at || 0).getTime()) / 1000,
    );
    // Keep slow loops (same road chakkar). Drop only GPS jitter + teleport jumps.
    if (km < 0.006) continue;
    if (km > Math.max(4.5, (dt / 3600) * 160)) continue;
    out.push(points[i]);
  }
  return out;
};

const trailKm = (points) =>
  (points || []).slice(1).reduce((sum, p, i) => sum + (haversineKm(points[i], p) || 0), 0);

const cropTrailToJobEnd = (points, endedAt) => {
  if (!endedAt || !points?.length) return points || [];
  const ts = new Date(endedAt).getTime();
  if (!Number.isFinite(ts)) return points;
  const kept = points.filter((p) => {
    const t = new Date(p.created_at || 0).getTime();
    return !Number.isFinite(t) || t <= ts + 2 * 60 * 1000;
  });
  return kept.length ? kept : points;
};

/** First time partner reached pickup, then leftover GPS until complete. */
const splitTrailAtReach = (points, pickup, reachedAt) => {
  const pts = points || [];
  if (pts.length < 2) return { toReach: pts, afterReach: [] };
  let idx = -1;
  if (reachedAt) {
    const ts = new Date(reachedAt).getTime();
    if (Number.isFinite(ts)) {
      idx = pts.findIndex((p) => new Date(p.created_at || 0).getTime() >= ts);
      if (idx < 0) idx = pts.length - 1;
    }
  }
  if (idx < 0 && pickup) {
    idx = pts.findIndex((p) => {
      const d = haversineKm(p, pickup);
      return d != null && d <= 0.22;
    });
  }
  if (idx < 0) return { toReach: pts, afterReach: [] };
  return {
    toReach: pts.slice(0, idx + 1),
    afterReach: pts.slice(idx),
  };
};

const pathColor = (t) => {
  const x = Math.max(0, Math.min(1, t));
  const from = [147, 197, 253];
  const to = [30, 64, 175];
  const r = Math.round(from[0] + (to[0] - from[0]) * x);
  const g = Math.round(from[1] + (to[1] - from[1]) * x);
  const b = Math.round(from[2] + (to[2] - from[2]) * x);
  return `rgb(${r},${g},${b})`;
};

const loadGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (window.__rsGoogleMapsPromise) return window.__rsGoogleMapsPromise;

  window.__rsGoogleMapsPromise = (async () => {
    const { data, error } = await db().rpc('get_google_api_key');
    const key = typeof data === 'string' ? data.trim() : '';
    if (error || !key || key.includes('your_google') || key.includes('YOUR_ACTUAL')) {
      throw new Error('Google Maps key missing');
    }

    await new Promise((resolve, reject) => {
      const existing = document.getElementById('rs-google-maps-js');
      if (existing) {
        if (window.google?.maps?.Map) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google Maps failed')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.id = 'rs-google-maps-js';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=en&region=IN`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(script);
    });

    if (!window.google?.maps?.Map) throw new Error('Google Maps unavailable');
    return window.google.maps;
  })();

  return window.__rsGoogleMapsPromise;
};

const routeViaGoogle = (gmaps, points) => {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return Promise.resolve(null);
  const service = new gmaps.DirectionsService();
  return new Promise((resolve) => {
    service.route(
      {
        origin: pts[0],
        destination: pts[pts.length - 1],
        waypoints: pts.slice(1, -1).slice(0, 23).map((p) => ({ location: p, stopover: false })),
        travelMode: gmaps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          resolve(null);
          return;
        }
        const route = result.routes[0];
        const latlngs = route.overview_path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        const km = (route.legs || []).reduce((sum, leg) => sum + (leg.distance?.value || 0), 0) / 1000;
        resolve({ latlngs, km });
      },
    );
  });
};

const extraColor = (t) => {
  const x = Math.max(0, Math.min(1, t));
  const from = [253, 186, 116];
  const to = [194, 65, 12];
  const r = Math.round(from[0] + (to[0] - from[0]) * x);
  const g = Math.round(from[1] + (to[1] - from[1]) * x);
  const b = Math.round(from[2] + (to[2] - from[2]) * x);
  return `rgb(${r},${g},${b})`;
};

const pinIcon = (gmaps, color) => ({
  path: 'M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z',
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 1.4,
  scale: 1.5,
  anchor: new gmaps.Point(12, 22),
  labelOrigin: new gmaps.Point(12, 9),
});

export default function ComplaintMechanicMap({
  complaint,
  mechanicRow,
  bargainPartners = [],
  trackMechanicId: trackMechanicIdProp,
  onTrackMechanicIdChange,
  embedded = false,
  compact = false,
}) {
  const queryClient = useQueryClient();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef({
    markers: [],
    lines: [],
    mechanic: null,
    pulse: null,
    accuracy: null,
    remain: null,
    fitKey: '',
    trailKey: '',
    remainKey: '',
  });
  const followLiveRef = useRef(true);
  const [mapError, setMapError] = useState('');
  const [trackLocal, setTrackLocal] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [followLive, setFollowLive] = useState(true);
  const [nowTick, setNowTick] = useState(Date.now());
  const [googleKm, setGoogleKm] = useState({ traveled: null, remaining: null });
  followLiveRef.current = followLive;

  const status = String(complaint?.request_status || complaint?.status || '').toLowerCase();
  const isCancelled = status.includes('cancel');
  const isClosed = isCancelled || status.includes('complete') || status.includes('closed');
  const assignedId = complaint?.assigned_mechanic_id ? String(complaint.assigned_mechanic_id) : '';
  const trackMechanicId = String(trackMechanicIdProp || trackLocal || assignedId || bargainPartners[0]?.id || '');
  const setTrackMechanicId = (id) => {
    setTrackLocal(id);
    onTrackMechanicIdChange?.(id);
  };
  const trackingAssigned = !!assignedId && trackMechanicId === assignedId;
  const trackedBargain = bargainPartners.find((p) => String(p.id) === trackMechanicId) || null;
  const isLive = !isClosed && (!!assignedId || bargainPartners.length > 0);

  const customerPin = useMemo(() => {
    return (
      parseCoord(complaint?.pickup_latitude, complaint?.pickup_longitude) ||
      parseCoord(complaint?.latitude, complaint?.longitude) ||
      extractLatLngFromText(complaint?.location_link) ||
      extractLatLngFromText(complaint?.pickup_address) ||
      extractLatLngFromText(complaint?.address)
    );
  }, [complaint]);

  const dropPin = useMemo(() => {
    const t = `${complaint?.service_type || ''} ${complaint?.problem || ''}`.toLowerCase();
    const towing =
      t.includes('towing') ||
      t.includes('tow ') ||
      t.startsWith('tow') ||
      t.includes('flatbed') ||
      t.includes('recovery van') ||
      t.includes('crane');
    if (!towing) return null;
    return (
      parseCoord(complaint?.drop_latitude, complaint?.drop_longitude) ||
      extractLatLngFromText(complaint?.drop_location)
    );
  }, [complaint]);

  const destPin = useMemo(() => {
    const reached = !!(complaint?.mechanic_reached_datetime || complaint?.arrival_otp_verified_at);
    if (reached && dropPin) return dropPin;
    return customerPin;
  }, [complaint, dropPin, customerPin]);

  const shopPin = useMemo(() => {
    if (trackingAssigned) {
      return parseCoord(mechanicRow?.service_center_lat, mechanicRow?.service_center_lng);
    }
    return parseCoord(trackedBargain?.service_center_lat, trackedBargain?.service_center_lng);
  }, [mechanicRow, trackingAssigned, trackedBargain]);

  const livePin = useMemo(() => {
    if (trackingAssigned) {
      return (
        parseCoord(complaint?.partner_live_lat, complaint?.partner_live_lng) ||
        parseCoord(complaint?.mechanic_latitude, complaint?.mechanic_longitude)
      );
    }
    return parseCoord(trackedBargain?.live_lat, trackedBargain?.live_lng);
  }, [complaint, trackingAssigned, trackedBargain]);

  const { data: liveRow } = useQuery({
    queryKey: ['complaintPartnerLive', complaint?.id],
    queryFn: async () => {
      const { data, error } = await db()
        .from('service_requests')
        .select('partner_live_lat, partner_live_lng, partner_live_updated_at')
        .eq('id', complaint.id)
        .maybeSingle();
      if (error) return null;
      return data;
    },
    enabled: !!complaint?.id && isLive && trackingAssigned,
    refetchInterval: isLive ? 3000 : false,
  });

  const { data: pings = [] } = useQuery({
    queryKey: ['complaintLocationTrail', complaint?.id],
    queryFn: async () => {
      const { data, error } = await db()
        .from('rsa_job_location_pings')
        .select('id, latitude, longitude, created_at, mechanic_id')
        .eq('service_request_id', complaint.id)
        .order('created_at', { ascending: true })
        .limit(1500);
      if (error) return [];
      return (data || [])
        .map((p) => ({
          ...p,
          mechanic_id: p.mechanic_id ? String(p.mechanic_id) : '',
          ...parseCoord(p.latitude, p.longitude),
        }))
        .filter((p) => p.lat != null);
    },
    enabled: !!complaint?.id,
    refetchInterval: isLive ? 3000 : false,
  });

  const rawTrail = useMemo(() => {
    const raw = Array.isArray(pings) ? pings : [];
    let mine = trackMechanicId
      ? raw.filter((p) => !p.mechanic_id || p.mechanic_id === String(trackMechanicId))
      : raw;
    if (mine.length < 1 && raw.length > mine.length) mine = raw;
    return mine;
  }, [pings, trackMechanicId]);

  const trail = useMemo(() => cleanTrail(rawTrail), [rawTrail]);
  const jobEndedAt = complaint?.mechanic_completed_datetime || complaint?.completed_at || complaint?.cancelled_at;
  const reachedAt = complaint?.arrival_otp_verified_at || complaint?.mechanic_reached_datetime;
  const jobTrail = useMemo(
    () => cropTrailToJobEnd(trail, jobEndedAt),
    [trail, jobEndedAt],
  );
  const { toReach, afterReach } = useMemo(
    () => splitTrailAtReach(jobTrail, customerPin, reachedAt),
    [jobTrail, customerPin, reachedAt],
  );

  const lastRawPing = rawTrail.length ? rawTrail[rawTrail.length - 1] : null;
  const liveGps =
    parseCoord(liveRow?.partner_live_lat, liveRow?.partner_live_lng) || livePin;
  const pingAt = lastRawPing?.created_at ? new Date(lastRawPing.created_at).getTime() : 0;
  const liveAt = liveRow?.partner_live_updated_at
    ? new Date(liveRow.partner_live_updated_at).getTime()
    : complaint?.partner_live_updated_at
      ? new Date(complaint.partner_live_updated_at).getTime()
      : 0;
  const completePin = isClosed && jobTrail.length ? jobTrail[jobTrail.length - 1] : null;
  const liveMechanicPin =
    lastRawPing && liveGps
      ? liveAt >= pingAt
        ? { ...liveGps, created_at: liveRow?.partner_live_updated_at || lastRawPing.created_at }
        : lastRawPing
      : lastRawPing || liveGps;
  const mechanicPin = completePin || liveMechanicPin;
  const hasActualPath = jobTrail.length >= 2;
  const startPin = jobTrail[0] || shopPin;
  const traveledKm = trailKm(jobTrail);
  const extraTravelKm = trailKm(afterReach);
  const remainingFallback = haversineKm(mechanicPin, destPin);
  const remainingKm = googleKm.remaining ?? remainingFallback;
  const fromStartKm = haversineKm(startPin, destPin || customerPin);

  useEffect(() => {
    if (!isLive) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isLive]);

  useEffect(() => {
    if (!complaint?.id || !isLive) return undefined;
    const client = db();
    const ch = client
      .channel(`advisor-job-live-${complaint.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'service_requests', filter: `id=eq.${complaint.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['complaintPartnerLive', complaint.id] });
          queryClient.invalidateQueries({ queryKey: ['serviceAdvisorComplaintDetail', complaint.id] });
        },
      )
      .subscribe();
    return () => {
      client.removeChannel(ch);
    };
  }, [complaint?.id, isLive, queryClient]);

  useEffect(() => {
    let cancelled = false;
    if (!mapEl.current) return undefined;
    if (!customerPin && !mechanicPin && !dropPin) return undefined;

    loadGoogleMaps()
      .then(async (gmaps) => {
        if (cancelled || !mapEl.current) return;
        let map = mapRef.current;
        if (!map) {
          map = new gmaps.Map(mapEl.current, {
            center: customerPin || dropPin || mechanicPin,
            zoom: 14,
            mapTypeId: 'roadmap',
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            scaleControl: true,
            zoomControl: true,
            clickableIcons: false,
            gestureHandling: 'greedy',
          });
          map.addListener('dragstart', () => {
            followLiveRef.current = false;
            setFollowLive(false);
          });
          mapRef.current = map;
        }

        const overlays = overlaysRef.current;
        const trailKey = jobTrail.map((p) => p.id || `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join('|');
        const staticFitKey = [
          customerPin?.lat,
          customerPin?.lng,
          dropPin?.lat,
          dropPin?.lng,
          startPin?.lat,
          startPin?.lng,
          trailKey,
        ].join(':');
        const remainKey = [
          mechanicPin ? mechanicPin.lat.toFixed(3) : '',
          mechanicPin ? mechanicPin.lng.toFixed(3) : '',
          destPin?.lat,
          destPin?.lng,
          isClosed ? '1' : '0',
        ].join(':');
        const rebuildStatic = overlays.fitKey !== staticFitKey || !overlays.markers.length;

        const addMarker = (pos, title, color, label, zIndex) => {
          const marker = new gmaps.Marker({
            map,
            position: pos,
            title,
            icon: pinIcon(gmaps, color),
            label: label
              ? { text: label, color: '#fff', fontSize: '10px', fontWeight: '700' }
              : undefined,
            zIndex: zIndex || (title === 'Mechanic' ? 900 : 600),
          });
          overlays.markers.push(marker);
          return marker;
        };

        const addLine = (path, color, dashed) => {
          if (!path?.length) return null;
          const line = new gmaps.Polyline({
            map,
            path,
            strokeColor: color,
            strokeOpacity: dashed ? 0 : 0.92,
            strokeWeight: dashed ? 4 : 5,
            icons: dashed
              ? [
                  {
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 3 },
                    offset: '0',
                    repeat: '14px',
                  },
                ]
              : undefined,
          });
          overlays.lines.push(line);
          return line;
        };

        const paintPath = (pts, colorFn, arrowFill, zBase) => {
          const drivenPath = (pts || []).map((p) => ({ lat: p.lat, lng: p.lng }));
          if (drivenPath.length < 2) return drivenPath;
          const under = new gmaps.Polyline({
            map,
            path: drivenPath,
            geodesic: true,
            strokeColor: colorFn(0),
            strokeOpacity: 0.35,
            strokeWeight: 10,
            zIndex: zBase,
          });
          overlays.lines.push(under);
          const bands = Math.min(10, drivenPath.length - 1);
          const step = Math.max(1, Math.ceil((drivenPath.length - 1) / bands));
          for (let i = 0; i < drivenPath.length - 1; i += step) {
            const chunk = drivenPath.slice(i, Math.min(i + step + 1, drivenPath.length));
            if (chunk.length < 2) break;
            const t = drivenPath.length <= 2 ? 1 : i / (drivenPath.length - 1);
            const seg = new gmaps.Polyline({
              map,
              path: chunk,
              geodesic: true,
              strokeColor: isCancelled ? '#ea580c' : colorFn(t),
              strokeOpacity: 0.92,
              strokeWeight: 5,
              zIndex: zBase + 20 + i,
            });
            overlays.lines.push(seg);
          }
          const arrows = new gmaps.Polyline({
            map,
            path: drivenPath,
            geodesic: true,
            strokeOpacity: 0,
            strokeWeight: 0,
            zIndex: zBase + 200,
            icons: [
              {
                icon: {
                  path: gmaps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  fillColor: arrowFill,
                  fillOpacity: 0.95,
                  strokeColor: '#ffffff',
                  strokeWeight: 0.6,
                },
                offset: '18px',
                repeat: '42px',
              },
            ],
          });
          overlays.lines.push(arrows);
          return drivenPath;
        };

        if (rebuildStatic) {
          overlays.markers.forEach((m) => {
            if (m !== overlays.mechanic) m.setMap(null);
          });
          overlays.lines.forEach((l) => {
            if (l !== overlays.remain) l.setMap(null);
          });
          overlays.markers = overlays.mechanic ? [overlays.mechanic] : [];
          overlays.lines = overlays.remain ? [overlays.remain] : [];

          const toPath = paintPath(toReach, pathColor, isCancelled ? '#9a3412' : '#1e3a8a', 350);
          const extraPath = paintPath(afterReach, extraColor, '#c2410c', 520);

          const dotsSrc = jobTrail;
          const dotEvery = Math.max(1, Math.ceil(dotsSrc.length / 40));
          dotsSrc.forEach((p, idx) => {
            if (idx !== 0 && idx !== dotsSrc.length - 1 && idx % dotEvery !== 0) return;
            const t = dotsSrc.length <= 1 ? 1 : idx / (dotsSrc.length - 1);
            const extra = afterReach.length > 1 && idx >= Math.max(0, toReach.length - 1);
            const dot = new gmaps.Circle({
              map,
              center: { lat: p.lat, lng: p.lng },
              radius: idx === 0 || idx === dotsSrc.length - 1 ? 28 : 16,
              fillColor: isCancelled ? '#ea580c' : extra ? extraColor(t) : pathColor(t),
              fillOpacity: 0.85,
              strokeColor: '#ffffff',
              strokeOpacity: 0.95,
              strokeWeight: 1.2,
              clickable: false,
              zIndex: 500,
            });
            overlays.lines.push(dot);
          });
          if (customerPin && dropPin) addLine([customerPin, dropPin], '#7c3aed', true);
          if (customerPin) addMarker(customerPin, 'Pickup', '#dc2626', 'P');
          if (dropPin) addMarker(dropPin, 'Drop', '#7c3aed', 'D');
          const startFar = startPin && mechanicPin && (haversineKm(startPin, mechanicPin) || 0) > 0.12;
          if (startFar) addMarker(startPin, 'Start', '#475569', 'S');

          const bounds = new gmaps.LatLngBounds();
          [customerPin, dropPin, mechanicPin, completePin, startFar ? startPin : null, ...toPath, ...extraPath]
            .filter(Boolean)
            .forEach((p) => bounds.extend(p));
          gmaps.event.trigger(map, 'resize');
          if (!bounds.isEmpty() && overlays.fitKey !== staticFitKey) {
            map.fitBounds(bounds, 64);
          }
          overlays.fitKey = staticFitKey;
          overlays.trailKey = trailKey;
        }

        if (mechanicPin) {
          const mechColor = isCancelled ? '#dc2626' : completePin ? '#c2410c' : '#059669';
          const mechLabel = completePin ? 'C' : 'M';
          const mechTitle = completePin ? 'Completed here' : 'Mechanic';
          if (overlays.mechanic) {
            overlays.mechanic.setPosition(mechanicPin);
            overlays.mechanic.setIcon(pinIcon(gmaps, mechColor));
            overlays.mechanic.setTitle(mechTitle);
            overlays.mechanic.setLabel({ text: mechLabel, color: '#fff', fontSize: '10px', fontWeight: '700' });
          } else {
            overlays.mechanic = addMarker(mechanicPin, mechTitle, mechColor, mechLabel, 1200);
          }
          if (overlays.pulse) {
            overlays.pulse.setCenter(mechanicPin);
          } else if (isLive) {
            overlays.pulse = new gmaps.Circle({
              map,
              center: mechanicPin,
              radius: 28,
              fillColor: '#10b981',
              fillOpacity: 0.22,
              strokeColor: '#059669',
              strokeOpacity: 0.85,
              strokeWeight: 2,
              clickable: false,
              zIndex: 1100,
            });
          }
          if (overlays.accuracy) {
            overlays.accuracy.setCenter(mechanicPin);
          } else if (isLive) {
            overlays.accuracy = new gmaps.Circle({
              map,
              center: mechanicPin,
              radius: 55,
              fillColor: '#10b981',
              fillOpacity: 0.08,
              strokeColor: '#059669',
              strokeOpacity: 0.35,
              strokeWeight: 1,
              clickable: false,
              zIndex: 500,
            });
          }
          if (followLiveRef.current && isLive) {
            map.panTo(mechanicPin);
          }
        }

        if (!isClosed && mechanicPin && destPin && overlays.remainKey !== remainKey) {
          const remain = await routeViaGoogle(gmaps, [mechanicPin, destPin]);
          if (cancelled) return;
          if (overlays.remain) {
            overlays.remain.setMap(null);
            overlays.lines = overlays.lines.filter((l) => l !== overlays.remain);
            overlays.remain = null;
          }
          const remainKm = remain?.km;
          const showRemain = remain?.latlngs?.length >= 2 && (remainKm == null || remainKm > 0.08);
          if (showRemain) {
            overlays.remain = addLine(remain.latlngs, '#0f766e', true);
          }
          overlays.remainKey = remainKey;
          setGoogleKm((prev) => ({
            traveled: prev.traveled,
            remaining: remain?.km ?? remainingFallback ?? null,
          }));
        }
      })
      .catch((e) => {
        if (!cancelled) setMapError(e.message || 'Google Maps failed to load');
      });

    return () => {
      cancelled = true;
    };
  }, [
    customerPin?.lat,
    customerPin?.lng,
    dropPin?.lat,
    dropPin?.lng,
    destPin?.lat,
    destPin?.lng,
    mechanicPin?.lat,
    mechanicPin?.lng,
    startPin?.lat,
    startPin?.lng,
    jobTrail,
    toReach,
    afterReach,
    completePin?.lat,
    completePin?.lng,
    isCancelled,
    isClosed,
    isLive,
    hasActualPath,
    remainingFallback,
  ]);

  useEffect(() => {
    if (!isLive) return undefined;
    let r = 26;
    let growing = true;
    const id = setInterval(() => {
      const pulse = overlaysRef.current.pulse;
      if (!pulse) return;
      r += growing ? 1.6 : -1.6;
      if (r >= 48) growing = false;
      if (r <= 22) growing = true;
      pulse.setRadius(r);
      pulse.setOptions({ fillOpacity: growing ? 0.16 : 0.28 });
    }, 90);
    return () => clearInterval(id);
  }, [isLive, mechanicPin?.lat, mechanicPin?.lng]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  useEffect(() => {
    const t = setTimeout(() => {
      const gmaps = window.google?.maps;
      const map = mapRef.current;
      if (!gmaps || !map) return;
      gmaps.event.trigger(map, 'resize');
    }, 80);
    return () => clearTimeout(t);
  }, [fullscreen]);

  useEffect(
    () => () => {
      overlaysRef.current.markers.forEach((m) => m.setMap(null));
      overlaysRef.current.lines.forEach((l) => l.setMap(null));
      overlaysRef.current.pulse?.setMap(null);
      overlaysRef.current.accuracy?.setMap(null);
      overlaysRef.current = {
        markers: [],
        lines: [],
        mechanic: null,
        pulse: null,
        accuracy: null,
        remain: null,
        fitKey: '',
        trailKey: '',
        remainKey: '',
      };
      mapRef.current = null;
    },
    [],
  );

  if (!customerPin && !mechanicPin && !dropPin) {
    return (
      <div className={embedded ? 'py-4 text-center' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
        {!embedded ? (
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={20} className="text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900">Mechanic map</h2>
          </div>
        ) : null}
        <p className="text-sm text-gray-500">
          Is complaint par GPS pin nahi hai (customer location / mechanic live). Naye assigned jobs par partner
          app live location bhejti hai.
        </p>
      </div>
    );
  }

  const assignedName =
    mechanicRow?.display_name || mechanicRow?.shop_name || mechanicRow?.mechanic_name || '';
  const lastUpdate =
    liveRow?.partner_live_updated_at ||
    (trackingAssigned && complaint?.partner_live_updated_at) ||
    trackedBargain?.live_updated_at ||
    lastRawPing?.created_at;
  const freshness = liveFreshness(lastUpdate, nowTick, isClosed);
  const showRemainStat = !isClosed && remainingKm != null && remainingKm > 0.08;
  const remainLabel = dropPin && destPin === dropPin ? 'To drop' : 'To pickup';
  const showTrackSelect = !isClosed && (assignedId || bargainPartners.length > 0);
  const pickupDropKm = haversineKm(customerPin, dropPin);
  const pickupDropRouteHref =
    customerPin && dropPin
      ? `https://www.google.com/maps/dir/?api=1&origin=${customerPin.lat},${customerPin.lng}&destination=${dropPin.lat},${dropPin.lng}&travelmode=driving`
      : '';
  const openMapsHref = pickupDropRouteHref
    ? pickupDropRouteHref
    : mechanicPin && destPin
      ? `https://www.google.com/maps/dir/${mechanicPin.lat},${mechanicPin.lng}/${destPin.lat},${destPin.lng}`
      : mechanicPin && customerPin
        ? `https://www.google.com/maps/dir/${mechanicPin.lat},${mechanicPin.lng}/${customerPin.lat},${customerPin.lng}`
        : customerPin
          ? `https://www.google.com/maps?q=${customerPin.lat},${customerPin.lng}`
          : mechanicPin
            ? `https://www.google.com/maps?q=${mechanicPin.lat},${mechanicPin.lng}`
            : '';

  const recenterOnMechanic = () => {
    setFollowLive(true);
    followLiveRef.current = true;
    const map = mapRef.current;
    if (map && mechanicPin) map.panTo(mechanicPin);
  };

  const shellClass = fullscreen
    ? 'fixed inset-0 z-[80] bg-white flex flex-col'
    : embedded
      ? 'flex flex-col'
      : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col';

  return (
    <div className={shellClass}>
      <div className={`flex items-center justify-between gap-2 ${fullscreen ? 'px-4 py-3 border-b border-slate-200' : 'mb-2'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {fullscreen || !embedded ? <Navigation size={18} className="text-teal-600 shrink-0" /> : null}
          {fullscreen || !embedded ? (
            <p className="text-sm font-semibold text-slate-900 truncate">Mechanic map</p>
          ) : null}
          {freshness === 'live' ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
              Live · {fmtAge(lastUpdate, nowTick) || 'now'}
            </span>
          ) : freshness === 'updating' ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
              Updating · {fmtAge(lastUpdate, nowTick)}
            </span>
          ) : freshness === 'gap' ? (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-800">
              Last GPS · {fmtAge(lastUpdate, nowTick)}
            </span>
          ) : freshness === 'waiting' && isLive ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
              Waiting GPS
            </span>
          ) : isCancelled ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
              Cancelled
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
              Done
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && mechanicPin ? (
            <button
              type="button"
              onClick={recenterOnMechanic}
              className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                followLive
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Navigation size={13} />
              {followLive ? 'Following' : 'Follow'}
            </button>
          ) : null}
          <a
            href={openMapsHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <ExternalLink size={13} />
            {pickupDropRouteHref ? 'Open route' : 'Google Maps'}
          </a>
          <button
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {fullscreen ? 'Exit' : 'Full screen'}
          </button>
        </div>
      </div>

      {showTrackSelect ? (
        <div className={fullscreen ? 'px-4 pb-2' : 'mb-2'}>
          <select
            value={trackMechanicId}
            onChange={(e) => setTrackMechanicId(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800"
          >
            {assignedId ? (
              <option value={assignedId}>Assigned{assignedName ? ` · ${assignedName}` : ''}</option>
            ) : (
              <option value="">
                {bargainPartners.length ? 'Select quoting mechanic' : 'No mechanic quote yet'}
              </option>
            )}
            {bargainPartners
              .filter((p) => String(p.id) !== assignedId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.status === 'pending' ? 'Quoted' : p.status} ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                  {p.name ? ` · ${p.name}` : ''}
                </option>
              ))}
          </select>
        </div>
      ) : assignedName ? (
        <p className={`text-xs text-slate-500 ${fullscreen ? 'px-4 pb-2' : 'mb-2'}`}>
          Assigned · {assignedName}
        </p>
      ) : null}

      {mapError ? (
        <div className={`relative ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
          <iframe
            title="Google Maps"
            src={
              mechanicPin && customerPin
                ? `https://maps.google.com/maps?output=embed&saddr=${mechanicPin.lat},${mechanicPin.lng}&daddr=${customerPin.lat},${customerPin.lng}`
                : `https://maps.google.com/maps?output=embed&q=${(customerPin || mechanicPin).lat},${(customerPin || mechanicPin).lng}&z=14`
            }
            className={`${
              fullscreen ? 'h-full min-h-[320px]' : compact ? 'h-72' : embedded ? 'h-[420px]' : 'h-80'
            } w-full rounded-lg border border-slate-200`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <div className={`relative ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
          <div
            ref={mapEl}
            className={`${
              fullscreen
                ? 'h-full min-h-[320px] rounded-none border-0'
                : compact
                  ? 'h-72 rounded-lg border border-slate-200'
                  : embedded
                    ? 'h-[420px] rounded-lg border border-slate-200'
                    : 'h-80 rounded-lg border border-slate-200'
            } w-full overflow-hidden z-0`}
          />

          <div className="pointer-events-none absolute left-2 top-2 flex flex-wrap gap-1.5">
            <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
              {hasActualPath
                ? `Driven ${formatKm(traveledKm)} · ${jobTrail.length} GPS pings`
                : `Actual path nahi · ${jobTrail.length || 0} GPS ping`}
            </span>
            {extraTravelKm > 0.05 ? (
              <span className="rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 shadow-sm border border-amber-200">
                Extra travel {formatKm(extraTravelKm)}
              </span>
            ) : null}
            <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
              {isClosed
                ? 'Completed'
                : showRemainStat
                  ? `${remainLabel} ${formatKm(remainingKm)}`
                  : destPin && dropPin && destPin === dropPin
                    ? 'At drop'
                    : 'Reached'}
            </span>
            {fromStartKm != null ? (
              <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
                From start {formatKm(fromStartKm)}
              </span>
            ) : null}
            {pickupDropKm != null ? (
              <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-violet-800 shadow-sm border border-violet-200">
                Pickup → drop {formatKm(pickupDropKm)}
              </span>
            ) : null}
          </div>

          <div className="pointer-events-none absolute left-2 bottom-8 rounded-lg bg-white/95 px-2.5 py-2 text-[11px] text-slate-700 shadow-sm border border-slate-200 max-w-[220px]">
            {hasActualPath ? (
              <>
                <p className="flex items-center gap-1.5">
                  <span className="inline-block h-0.5 w-5 rounded bg-sky-300" />
                  <span className="inline-block h-0.5 w-5 rounded bg-blue-800" />
                  GPS path (light = start, dark = now)
                </p>
                <p className="mt-1 text-[10px] text-slate-500">
                  Blue = pickup tak. Orange = pahunchne ke baad extra travel / complete.
                </p>
              </>
            ) : (
              <p className="text-amber-800 font-semibold">
                Partner app ne is job ka rasta record nahi kiya. Shop–customer line guess hoti, isliye nahi dikhai.
              </p>
            )}
            {showRemainStat && !isClosed ? (
              <p className="mt-1 flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-5 rounded border-t-2 border-dashed border-teal-700" />
                {remainLabel}
              </p>
            ) : null}
            <p className="mt-1 flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-red-600" />
              Pickup
            </p>
            {dropPin ? (
              <p className="mt-1 flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-violet-700" />
                Drop
              </p>
            ) : null}
            <p className="mt-1 flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-teal-700" />
              Mechanic
            </p>
            {lastUpdate ? (
              <p className="mt-1.5 text-[10px] text-slate-500">
                GPS {fmtAge(lastUpdate, nowTick) || fmtTime(lastUpdate)}
                {freshness === 'gap'
                  ? ' · Partner app se naya ping nahi aa raha (app band / background). Job open hai — ping aate hi live ho jayega.'
                  : ''}
              </p>
            ) : isLive ? (
              <p className="mt-1.5 text-[10px] text-amber-700">Partner app se live GPS wait ho raha hai</p>
            ) : null}
          </div>

          {fullscreen ? (
            <button
              type="button"
              onClick={() => setFullscreen(false)}
              className="absolute right-2 top-2 inline-flex items-center justify-center rounded-lg bg-white/95 p-2 text-slate-700 shadow-sm border border-slate-200 hover:bg-white"
              aria-label="Close full screen"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
